<template>
  <div class="paid-collaborations-comparison">
    <div
      v-if="shouldShowResults"
      class="extra-large-page scrollable-x"
    >
      <compare-table :items="items" />
    </div>

    <div
      v-else
      class="standard-page"
    >
      <v-card
        outlined
        class="d-flex justify-space-between align-center box-shadow-md"
      >
        <div>
          <v-card-title>
            Compare Paid Collaborations
          </v-card-title>

          <v-card-subtitle>
            Compare upto 3 filters at once.
          </v-card-subtitle>
        </div>

        <v-card-actions class="pr-4">
          <v-btn
            color="primary"
            depressed
            @click="handleSearch"
            :disabled="isLoading"
          >
            <v-icon left>search</v-icon>
            Compare
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card
        v-for="(item, index) in items"
        :key="item"
        class="box-shadow-md mt-3"
        outlined
      >
        <v-card-text>
          <!-- show all possible options for search/filter -->
          <filter-options :module-id="item" />

          <!-- view the chips for selected/applied filters -->
          <applied-filters
            :filters="appliedFilters[index]"
            @remove="(data) => handleRemove(item, data)"
          />
        </v-card-text>
      </v-card>

      <!-- show an option to add another item for this comparison -->
      <div class="d-flex justify-center py-6">
        <v-btn
          @click="handleCTAClick"
          class="shadow--primary"
          color="primary"
          type="submit"
          depressed
        >
        <v-icon left>
          {{ items.length < maxItems ? 'add' : 'remove' }}
        </v-icon>

         {{ items.length < maxItems ? 'Compare Another' : 'Remove Last Comparison' }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
const FilterOptions = () => import("@/components/paid-collaborations/FilterOptions.vue")
const AppliedFilters = () => import("@/components/common/AppliedFilters.vue")
const CompareTable = () => import('@/components/paid-collaborations-comparison/CompareTable.vue')

export default {
  name: "PaidCollaborationsComparison",

  components: {
    FilterOptions,
    AppliedFilters,
    CompareTable
  },

  data: () => ({
    loadingItems: [],

    minItems: 2,
    maxItems: 3,

    // three different symbol IDs
    items: []
  }),

  computed: {
    // disable the search button once the requests are in queue
    isLoading() {
      return this.loadingItems.length !== 0
    },

    // get the applied filters from vuex store
    appliedFilters() {
      return this.items.map((id) => {
        return this.$store.getters[`paidCollaborations/filters`](id).sort((a, b) => a.sort - b.sort)
      })
    },

    // find out if all the results have been found
    shouldShowResults() {
      return this.items.map((id) => {
        return this.$store.getters['paidCollaborations/hasAllBeenLoaded'](id)
      }).filter((value) => Boolean(value)).length === this.items.length
    }
  },

  methods: {
    async dispatchSearch(id) {
      // Add it to the loading items array
      this.loadingItems.push(id)

      await this.$store.dispatch("paidCollaborations/searchPosts", id)

      // once the data has been loaded, remove it from the loading items array
      const index = this.loadingItems.findIndex((search) => search === id)
      if (index !== -1) {
        this.loadingItems.splice(index, 1)
      }
    },

    /**
     * Dispatch the network requests to fetch all the search results
     */
    handleSearch() {
      // go through each of the items and fetch them in parallel
      for (const item of this.items) {
        this.dispatchSearch(item)
      }
    },

    /**
     * Remove the filter item
     *
     * @param {Symbol} id
     * @param {Object} data
     */
    handleRemove(id, data) {
      this.$store.dispatch(`paidCollaborations/removeFilter`, {
        id,
        filterId: data.id
      })
    },

    /**
     * Push more items for the comparison table or delete the last one
     */
    handleCTAClick() {
      if (this.items.length < this.maxItems) {
        this.items.push(Symbol())
      }
      else {
        this.items.splice(this.items.length - 1, 1)
      }
    }
  },

  mounted() {
    // Populate the items with minimum items that are required
    this.items = Array.from({ length: this.minItems }, () => Symbol())
  }
}
</script>

<style lang="stylus">
.paid-collaborations-comparison
  .scrollable-x
    overflow-x auto
</style>
